import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSuperadminAuth } from '../contexts/SuperadminAuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { LoginCredentials } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { SUPERADMIN_ROUTES } from '../routes/routes';

// Styled components matching the design
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(2, 4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 60%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 200px',
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',

  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
  // [theme.breakpoints.down('sm')]: {
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  // },
}));

const BRAND_COLOR = '#17A6DA';

const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6, 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));

const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd',
    },

    '&:hover fieldset': {
      borderColor: '#808080',
    },

    '&.Mui-focused fieldset': {
      borderColor: ' #808080',
      borderWidth: '2px',
    },
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

// Validation schema
const loginSchema = yup.object({
  email: yup.string().required('Email ID is required').email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

interface LocationState {
  from?: {
    pathname: string;
  };
}

// Initial form values (prefilled for superadmin testing)
const initialValues: LoginCredentials = {
  email: 'superadmin@waterpe.com',
  password: 'password123',
};

export const SuperadminLoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading } = useSuperadminAuth();

  const [showPassword, setShowPassword] = useState(false);

  // Get redirect location
  const from = (location.state as LocationState)?.from?.pathname || SUPERADMIN_ROUTES.DASHBOARD;

  // Handle form submission
  const handleSubmit = async (
    values: LoginCredentials,
    { setFieldError }: { setFieldError: (field: string, message: string) => void }
  ) => {
    try {
      await login(values);
      navigate(from, { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setFieldError('email', errorMessage);
    }
  };

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      {/* Left Panel - Login Form */}
      <LeftPanel>
        {/* Brand Header */}

        <FormSection>
          {/* Welcome Section */}
          <WelcomeSection>
            <Typography
              variant="h1"
              weight="bold"
              color="textPrimary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)' }}
            >
              Superadmin Portal
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
            >
              Sign in to your GrowthBnk Superadmin account.
            </Typography>
          </WelcomeSection>

          {/* Login Form */}
          <Box
            sx={{
              width: '70%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={loginSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Email ID Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Email ID"
                          name="email"
                          type="text"
                          placeholder="Enter Email ID"
                          value={values.email}
                          onChange={e => setFieldValue('email', e.target.value)}
                          onBlur={() => setFieldTouched('email', true)}
                          onFocus={() => {
                            if (!touched.email) {
                              setFieldTouched('email', true);
                            }
                          }}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Password"
                          name="password"
                          type="password"
                          placeholder="Enter Password"
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onBlur={() => setFieldTouched('password', true)}
                          onFocus={() => {
                            if (!touched.password) {
                              setFieldTouched('password', true);
                            }
                          }}
                          error={touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showPassword}
                          onPasswordToggle={() => setShowPassword(!showPassword)}
                          sx={inputSx}
                        />
                      </Box>

                      {/* Login Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        fullWidth
                        loading={loading}
                        loadingText="Signing in..."
                        disabled={!values.email || !values.password}
                      >
                        Login to Superadmin
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Login Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Superadmin Login Banner" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default SuperadminLoginForm;
